import { useEffect, useRef, useState } from 'react';
import { config } from '../../config/config';
import { TokenStatsType } from './useTokenStats';

const startValues: Record<string, { price: number; priceChangePercent: number }> = {
    BTCUSDC: { price: 65000, priceChangePercent: 2.5 },
    ETHUSDC: { price: 3500, priceChangePercent: 2 },
    LINKUSDC: { price: 15, priceChangePercent: 1.5 },
    BNBUSDC: { price: 600, priceChangePercent: 1.8 },
    XRPUSDC: { price: 0.5, priceChangePercent: 1.2 },
    ADAUSDC: { price: 0.4, priceChangePercent: 1 },
    MATICUSDC: { price: 0.7, priceChangePercent: 1.3 },
    DOTUSDC: { price: 7, priceChangePercent: 1.6 },
    ARBUSDC: { price: 1.2, priceChangePercent: 2 },
    DOGEUSDC: { price: 0.15, priceChangePercent: 3 },
    UNIUSDC: { price: 10, priceChangePercent: 1.7 },
};

const useMockTokenStats = ({
    priceMin = 10,
    priceMax = 75000,
    percentMin = -5,
    percentMax = 5,
    intervalMs = 1000,
    priceMaxChangePercent = 0.02,
    percentMaxChangePercent = 0.5,
}: {
    priceMin?: number;
    priceMax?: number;
    percentMin?: number;
    percentMax?: number;
    intervalMs?: number;
    priceMaxChangePercent?: number;
    percentMaxChangePercent?: number;
} = {}) => {
    const [tokenStats, setTokenStats] = useState<Record<string, TokenStatsType>>(() => {
        const initial: Record<string, TokenStatsType> = {};
        config.chartSymbols.forEach((chartSymbol) => {
            const start = startValues[chartSymbol];
            if (start) {
                initial[chartSymbol] = {
                    price: start.price.toString(),
                    priceChangePercent: start.priceChangePercent,
                };
            } else {
                initial[chartSymbol] = {
                    price: ((priceMin + priceMax) / 2).toString(),
                    priceChangePercent: (percentMin + percentMax) / 2,
                };
            }
        });
        return initial;
    });

    const tokenStatsRef = useRef(tokenStats);

    useEffect(() => {
        const interval = setInterval(() => {
            const updated: Record<string, TokenStatsType> = {};
            Object.entries(tokenStatsRef.current).forEach(([symbol, stats]) => {
                const price = parseFloat(stats.price);
                const maxPriceChange = price * priceMaxChangePercent;
                const priceChange = (Math.random() * 2 - 1) * maxPriceChange;
                const newPrice = Math.max(priceMin, Math.min(priceMax, price + priceChange));

                const maxPercentChange = (percentMax - percentMin) * percentMaxChangePercent;
                const percentChange = (Math.random() * 2 - 1) * maxPercentChange;
                const newPercent = Math.max(
                    percentMin,
                    Math.min(percentMax, stats.priceChangePercent + percentChange)
                );

                updated[symbol] = {
                    price: newPrice.toString(),
                    priceChangePercent: newPercent,
                };
            });
            tokenStatsRef.current = updated;
            setTokenStats({ ...updated });
        }, intervalMs);

        return () => clearInterval(interval);
    }, [priceMin, priceMax, percentMin, percentMax, intervalMs, priceMaxChangePercent, percentMaxChangePercent]);

    return tokenStats;
};

export default useMockTokenStats;
